import useClientContext, { setCurrentEntry, setModal } from "@hooks/useClientContext";
import useSession from "@hooks/useSession";
import {
  ListboxButton,
  ListboxInput,
  ListboxList,
  ListboxOption,
  ListboxPopover,
} from "@reach/listbox";
import "@reach/listbox/styles.css";
import { hexRGBA, media, publicBasePath, pxToRem } from "src/styles/styleUtils";
import styled from "styled-components";
import { ButtonBase } from "./Button";

const EntryName = styled.p`
  margin: 0;
`;

const ListBoxStyled = styled(ListboxInput)`
  position: relative;

  [data-reach-listbox-button] {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: ${pxToRem(8)} ${pxToRem(36)} ${pxToRem(8)} 0;
    display: block;

    border: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    outline: none;

    &:focus-visible [data-reach-listbox-arrow] {
      outline: ${pxToRem(2)} solid ${({ theme }) => hexRGBA(theme.blue, 0.75)};
      outline-offset: ${pxToRem(-6)};
    }
  }
  [data-reach-listbox-arrow] {
    margin: 0;
    padding: ${pxToRem(4)};
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: calc(50% - 15px);
    right: 0px;
    color: ${({ theme }) => theme.grey_500};

    ${media.tablet} {
      padding: ${pxToRem(2)};
    }

    svg {
      width: ${pxToRem(24)};
      height: ${pxToRem(24)};
    }
  }
`;

const ListboxPopoverStyled = styled(ListboxPopover)`
  margin-top: ${pxToRem(8)};
  padding: 0;
  border: ${pxToRem(2)} solid white;
  border-top-width: 0;
  border-radius: 0 0 ${pxToRem(4)} ${pxToRem(4)};

  ${media.tablet} {
    padding: ${pxToRem(2)} 0;
    box-shadow: 0px 4px 8px 2px ${({ theme }) => hexRGBA(theme.grey_700, 0.3)};
    border-radius: ${pxToRem(4)};
  }

  &:focus-within {
    outline: none;
    box-shadow: none;
    ${media.tablet} {
      box-shadow: 0px 4px 8px 2px ${({ theme }) => hexRGBA(theme.grey_700, 0.3)};
    }
  }

  [data-reach-listbox-option] {
    font-size: ${pxToRem(21)};
    padding: 0.5rem 1.5rem;
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.fontSecondary};
    cursor: pointer;

    &[data-current-nav] {
      background-color: ${({ theme }) => hexRGBA(theme.grey_100, 0.25)};
      color: black;
    }

    &[data-current-selected] {
      font-weight: normal;

      > div {
        position: relative;
        &:after {
          position: absolute;
          content: "";
          width: ${pxToRem(6)};
          height: ${pxToRem(6)};
          background-color: ${({ theme }) => theme.blue};
          border-radius: 50%;
          right: ${pxToRem(-16)};
        }
      }
    }
  }
`;
const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  img {
    margin-right: ${pxToRem(8)};
  }
`;
const ActionButtonArea = styled.div`
  padding: ${pxToRem(2)} 0 ${pxToRem(1)};
  border-top: ${pxToRem(1)} solid ${({ theme }) => hexRGBA(theme.grey_100, 0.25)};
  display: flex;
  align-items: center;

  div {
    width: ${pxToRem(1)};
    height: ${pxToRem(18)};
    background-color: ${({ theme }) => hexRGBA(theme.grey_100, 0.4)};
  }

  button {
    ${ButtonBase}
    flex: 0 1 50%;
    margin: ${pxToRem(0)} ${pxToRem(1)};
    padding: ${pxToRem(8)} 0;
    font-weight: bold;
    font-size: ${pxToRem(12)};
    text-transform: unset;
    border-radius: ${pxToRem(4)};
    transition: background 0.2s ease-in-out;

    &:not(:disabled) {
      &:hover,
      :active,
      :focus {
        background-color: ${({ theme }) => hexRGBA(theme.grey_100, 0.25)};
      }
    }
  }
`;

function EntryBarListBox({ isLeagueMemberView, leagueMemberEntry }) {
  const { data: session } = useSession();
  const [{ currentEntry }, dispatch] = useClientContext();
  const entries = session.entries.chase;
  const entryId = currentEntry?.id;
  const isCreateBtnDisabled = session.user.chase_entries_count >= 3;

  return (
    <>
      {!isLeagueMemberView ? (
        <ListBoxStyled
          value={`${entryId}`}
          onChange={(value) =>
            dispatch(setCurrentEntry(entries.find((e) => e.id === Number(value)) || currentEntry))
          }
        >
          <ListboxButton
            arrow={
              /* chevronDown.svg as a ReactNode */
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            }
            aria-label="Entry Toggle &amp; Options"
          >
            {currentEntry?.name}
          </ListboxButton>
          <ListboxPopoverStyled>
            <ListboxList>
              {entries.map((e) => (
                <ListboxOption key={e.id} value={`${e.id}`}>
                  <OptionWrapper>
                    <img
                      src={`${publicBasePath}/images/avatar/${e.avatar_id ?? 50}.png`}
                      alt="Entry Avatar"
                      width="28"
                      height="28"
                    />{" "}
                    {e.name}
                  </OptionWrapper>
                </ListboxOption>
              ))}
              <ActionButtonArea>
                <button onClick={() => dispatch(setModal("EDIT_ENTRY"))}>Edit Entry</button>
                <div aria-hidden="true" role="presentation" />
                <button
                  disabled={isCreateBtnDisabled}
                  onClick={() => dispatch(setModal("CREATE_ENTRY"))}
                >
                  Create Entry
                </button>
              </ActionButtonArea>
            </ListboxList>
          </ListboxPopoverStyled>
        </ListBoxStyled>
      ) : (
        <EntryName>{leagueMemberEntry?.name}</EntryName>
      )}
    </>
  );
}

export default EntryBarListBox;
